import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, BookOpen, Lock, Unlock } from 'lucide-react';
import { booksAPI, packagesAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ManageBooks = () => {
  const [books, setBooks] = useState([]);
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    is_free: false,
    pdf_url: '',
    drive_link: '',
    required_package: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [booksRes, packagesRes] = await Promise.all([
        booksAPI.getAll(),
        packagesAPI.getAll(false),
      ]);
      setBooks(booksRes.data);
      setPackages(packagesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (book = null) => {
    if (book) {
      setEditingBook(book);
      setFormData({
        title: book.title,
        description: book.description,
        cover_image: book.cover_image || '',
        is_free: book.is_free,
        pdf_url: book.pdf_url || '',
        drive_link: book.drive_link || '',
        required_package: book.required_package || '',
      });
    } else {
      setEditingBook(null);
      setFormData({
        title: '',
        description: '',
        cover_image: '',
        is_free: false,
        pdf_url: '',
        drive_link: '',
        required_package: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        required_package: formData.required_package || null,
        pdf_url: formData.pdf_url || null,
        drive_link: formData.drive_link || null,
        cover_image: formData.cover_image || null,
      };

      if (editingBook) {
        await booksAPI.update(editingBook.id, data);
      } else {
        await booksAPI.create(data);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save book');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this book?')) return;
    
    try {
      await booksAPI.delete(id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete book');
    }
  };

  const toggleFree = async (book) => {
    try {
      await booksAPI.update(book.id, { is_free: !book.is_free });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update book');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Manage Books</h1>
          <p className="text-gray-400">Add, edit, and manage books with access control</p>
        </div>
        <Button onClick={() => openModal()} className="bg-purple-600 hover:bg-purple-700">
          <Plus size={18} className="mr-2" />
          Add Book
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {books.map((book) => (
            <div
              key={book.id}
              className="bg-[#1a1a2e] rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all"
            >
              <div className="relative aspect-[2/3]">
                <img
                  src={book.cover_image || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=200&h=300&fit=crop'}
                  alt={book.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <Badge className={book.is_free ? 'bg-green-600' : 'bg-purple-600'}>
                    {book.is_free ? 'Free' : 'Paid'}
                  </Badge>
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-white font-semibold text-sm line-clamp-2 mb-2">{book.title}</h3>
                {book.required_package && (
                  <p className="text-gray-500 text-xs">Requires: {book.required_package}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleFree(book)}
                    className={book.is_free ? 'text-green-500' : 'text-yellow-500'}
                  >
                    {book.is_free ? <Unlock size={16} /> : <Lock size={16} />}
                  </Button>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openModal(book)}
                      className="text-gray-400 hover:text-white h-7 w-7"
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(book.id)}
                      className="text-gray-400 hover:text-red-500 h-7 w-7"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {books.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <BookOpen size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No books yet. Add your first book!</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen size={20} />
              {editingBook ? 'Edit Book' : 'Add New Book'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-gray-400">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="Book title"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white min-h-[80px]"
                placeholder="Book description"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Cover Image URL</Label>
              <Input
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="https://..."
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#12121a] rounded-lg">
              <Label className="text-gray-400">Free Book</Label>
              <Switch
                checked={formData.is_free}
                onCheckedChange={(checked) => setFormData({ ...formData, is_free: checked })}
              />
            </div>

            {formData.is_free ? (
              <div className="space-y-2">
                <Label className="text-gray-400">PDF URL (for free access)</Label>
                <Input
                  value={formData.pdf_url}
                  onChange={(e) => setFormData({ ...formData, pdf_url: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="https://example.com/book.pdf"
                />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-400">Google Drive Link (for paid access)</Label>
                  <Input
                    value={formData.drive_link}
                    onChange={(e) => setFormData({ ...formData, drive_link: e.target.value })}
                    className="bg-[#12121a] border-gray-700 text-white"
                    placeholder="https://drive.google.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Required Package (Optional)</Label>
                  <Select
                    value={formData.required_package || "any"}
                    onValueChange={(value) => setFormData({ ...formData, required_package: value === "any" ? "" : value })}
                  >
                    <SelectTrigger className="bg-[#12121a] border-gray-700 text-white">
                      <SelectValue placeholder="Any paid subscription" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-gray-700">
                      <SelectItem value="any" className="text-white hover:bg-purple-600/30">Any paid subscription</SelectItem>
                      {packages.map((pkg) => (
                        <SelectItem key={pkg.id} value={pkg.name} className="text-white hover:bg-purple-600/30">
                          {pkg.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
              <Save size={16} className="mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageBooks;
